// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useScript } from "@stanion/App/Widgets/ExternalScriptWidget/useScript";
import { useScriptCode } from "@stanion/App/Widgets/ExternalScriptWidget/useScriptCode";
import React from "react";

export const enum fields {
    addScriptSrc = "addScriptSrc",
    addScriptCode = "addScriptCode",
    scriptSrc = "scriptSrc",
    addToHead = "addToHead",
    codeSrc = "codeSrc",
    domain = "domain",
}

interface ExternalScriptWidgetProps extends WidgetProps {
    fields: {
        [fields.scriptSrc]: string;
        [fields.codeSrc]: string;
        [fields.domain]: string;
        [fields.addToHead]: boolean;
        [fields.addScriptCode]: boolean;
        [fields.addScriptSrc]: boolean;
    };
}

type Props = ExternalScriptWidgetProps;

const ExternalScriptWidget: React.FC<Props> = ({ fields }) => {
    useScript(fields.scriptSrc, fields.addToHead, fields.domain);
    useScriptCode(fields.codeSrc, fields.addToHead);
    return null;
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.addScriptSrc,
            displayName: "Add Script Src",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            dependentFields: [fields.scriptSrc],
            sortOrder: 1,
        },
        {
            displayName: "Script Src",
            name: fields.scriptSrc,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => item.fields.addScriptSrc,
            sortOrder: 2,
        },
        {
            displayName: "Script Domain",
            name: fields.domain,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            placeholder: "this field is required for oneTrust Scripts",
            isRequired: false,
            isVisible: item => item.fields.addScriptSrc,
            sortOrder: 3,
        },
        {
            name: fields.addScriptCode,
            displayName: "Add Script Code",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            dependentFields: [fields.codeSrc],
            sortOrder: 4,
        },
        {
            displayName: "script Code",
            name: fields.codeSrc,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => item.fields.addScriptCode,
            sortOrder: 5,
        },
        {
            name: fields.addToHead,
            displayName: "Add to Head Tag",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            sortOrder: 6,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ExternalScriptWidget,
    definition,
};

export default widgetModule;
